/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { TwoColumnedRowWithAvatar } from "../../components/TwoColumnedRowWithAvatar";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { SalesChannelListItem } from "./models/sales-channel";
import { MutateSalesChannelSelector } from "./MutateSalesChannelSelector";
import { usePushMenuOnSalesChannel } from "../menus/service/menus-api";
import { ButtonAction } from "../../components/NewButtons";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { toastr } from "react-redux-toastr";
import { ActionSpinner } from "../../components/ActionSpinner";
import { Shop } from "./model/ShopSuccessResponse";
import { getErrorMessage } from "../../error-handler";

const infoContainerStyle = css`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
`;
const infoTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

type Props = {
  salesChannel: SalesChannelListItem;
  showDivider: boolean;
  shop?: Shop;
};

const getFile = (platform: string) => {
  switch (platform) {
    case "Foodora":
      return require("../../assets/images/FoodoraLogo.png");
    case "JET":
      return require("../../assets/images/JustEatLogo.png");
    case "Wolt":
      return require("../../assets/images/WoltLogo.jpeg");
    default:
      return undefined;
  }
};

export const SalesChannelItem = ({
  salesChannel,
  showDivider,
  shop,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const {
    mutateAsync: pushMenuOnSalesChannel,
    isLoading: pushMenuIsLoading,
  } = usePushMenuOnSalesChannel();

  const pushCurrentMenu = async () => {
    try {
      await pushMenuOnSalesChannel({
        salesChannelId: salesChannel.id,
        organizationKeyName,
      });
      toastr.success("Success", "Menu successfully pushed");
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  return (
    <TwoColumnedRowWithAvatar
      title={salesChannel.platform}
      avatarPlaceholderText={salesChannel.name.charAt(0).toUpperCase()}
      showDivider={showDivider}
      file={getFile(salesChannel.platform)}
    >
      <div css={infoContainerStyle}>
        <div css={infoTextStyle}>
          <StyledText
            color={colors.textSecondary}
            className="bodySubtextWide mb-0"
          >
            Sales channel name
          </StyledText>
          <StyledText
            color={colors.textPrimary}
            className="bodyTextSemibold mb-0"
          >
            {salesChannel.name}
          </StyledText>
        </div>
        <div css={infoTextStyle}>
          <StyledText
            color={colors.textSecondary}
            className="bodySubtextWide mb-0"
          >
            Restaurant name
          </StyledText>
          <StyledText
            color={colors.textPrimary}
            className="bodyTextSemibold mb-0"
          >
            {salesChannel.shopName}
          </StyledText>
        </div>
        {salesChannel.menuPushable && (
          <ButtonAction onClick={pushCurrentMenu} buttonSize="small">
            {pushMenuIsLoading ? (
              <ActionSpinner />
            ) : (
              "Push current menu to sales channel"
            )}
          </ButtonAction>
        )}
      </div>
      <MutateSalesChannelSelector
        salesChannelId={salesChannel.id}
        shop={shop}
        type="update"
      />
    </TwoColumnedRowWithAvatar>
  );
};
